import React from 'react';
import { Client } from "../types";
import { 
  EnrichmentDialogState, 
  EnrichedData, 
  BackendProgressStage, 
  MappableClientField
} from './enrichmentTypes';
import { EnrichingDialogContent } from './EnrichingDialogContent';
import { EnrichmentErrorDialogContent } from './EnrichmentErrorDialogContent';
import { EnrichmentPendingDialogContent } from './EnrichmentPendingDialogContent';
import { EnrichmentResultsDisplay } from './EnrichmentResultsDisplay';
import { RawResponseDisplay } from './RawResponseDisplay';
import { EnrichmentFallbackDialogContent } from './EnrichmentFallbackDialogContent';
import { mappableFields as globalMappableFields } from '../enrichment-utils'; // renamed to avoid conflict
import { formatEstimatedCompletionTime } from '@/utils/formatters';

interface EnrichmentDialogContentSwitcherProps {
  dialogState: EnrichmentDialogState;
  client: Client;
  derivedMessage: string;
  backendProgressStage?: BackendProgressStage | string | null;
  derivedLutraRunId?: string | null;
  initialLutraResponse?: EnrichedData | null;
  rawDebugResponse?: EnrichedData | null;
  onClose: () => void; 
  errorDetails?: { error: string; details?: Record<string, unknown> | string | null; } | null;
  actualCompanyData?: EnrichedData | null;
  selectedFields?: Record<string, boolean>; // string should be keyof Client ideally
  handleToggleField?: (key: string) => void; // string should be keyof Client ideally
  estimatedCompletionAt?: string | null;
}

export const EnrichmentDialogContentSwitcher: React.FC<EnrichmentDialogContentSwitcherProps> = ({
  dialogState,
  client,
  derivedMessage,
  backendProgressStage,
  derivedLutraRunId,
  initialLutraResponse,
  rawDebugResponse,
  onClose, 
  errorDetails,
  actualCompanyData,
  selectedFields,
  handleToggleField,
  estimatedCompletionAt,
}) => {
  const formattedEstimatedTime = formatEstimatedCompletionTime(estimatedCompletionAt);

  // Use local mappableFields from enrichment-utils
  const mappableFieldsForDisplay: MappableClientField<keyof Client>[] = globalMappableFields;

  switch (dialogState) {
    case 'loading': // Corrected
      return (
        <EnrichingDialogContent
          clientName={client.name}
          message={derivedMessage}
          progressStage={backendProgressStage}
          estimatedCompletionTime={formattedEstimatedTime}
        />
      );
    // Consolidating pending-like states
    case 'queued':
    case 'requested':
    case 'initiated_lutra':
    case 'pending_lutra_data':
    case 'processing':
    case 'extracting':
    case 'finalizing':
    case 'polling_for_results':
    case 'processing_retrieved_data':
      return (
        <EnrichmentPendingDialogContent
          clientName={client.name}
          message={derivedMessage}
          lutraRunId={derivedLutraRunId}
          initialLutraResponse={initialLutraResponse}
          onClose={onClose}
          progressStage={backendProgressStage || dialogState} // Pass dialogState if progressStage is undefined
          estimatedCompletionTime={formattedEstimatedTime}
        />
      );
    case 'error_initiating':
    case 'error_polling':
    case 'error_processing':
    case 'error_lutra_failure':
    case 'error_unknown': // Corrected
      return (
        <EnrichmentErrorDialogContent
          clientName={client.name}
          error={errorDetails?.error || derivedMessage || 'An unknown error occurred.'}
          details={errorDetails?.details}
          rawResponse={rawDebugResponse}
          onClose={onClose}
          progressStage={backendProgressStage || dialogState}
        />
      );
    case 'success': // Corrected
      if (actualCompanyData && selectedFields && handleToggleField) {
        return (
          <div className="space-y-6">
            <p className="text-sm text-muted-foreground -mt-2 mb-4">
              Status: {derivedMessage}
              {derivedLutraRunId && <span className="block text-xs">Run ID: {derivedLutraRunId}</span>}
            </p>
            <EnrichmentResultsDisplay
              client={client}
              actualCompanyData={actualCompanyData}
              selectedFields={selectedFields as Record<keyof Client, boolean>} // Cast for safety
              handleToggleField={handleToggleField as (fieldKey: keyof Client) => void} // Cast for safety
              mappableFields={mappableFieldsForDisplay}
            />
            {initialLutraResponse && (
              <RawResponseDisplay
                title="Full Initial Enrichment Response (Debug)"
                data={initialLutraResponse}
              />
            )}
          </div>
        );
      }
      // Fallthrough to fallback if data is missing for success state
      return <EnrichmentFallbackDialogContent 
                clientName={client.name} 
                message="Data missing for success state. Raw data may be available below." 
                rawResponse={rawDebugResponse || initialLutraResponse || actualCompanyData}
             />;
    case 'applied': // Added state
      return (
        <EnrichmentFallbackDialogContent // Or a dedicated "Applied" content component
          clientName={client.name}
          message="Enrichment data has been successfully applied."
          rawResponse={null} // No raw response needed typically
        />
      );
    case 'cancelled': // Added state
      return (
        <EnrichmentFallbackDialogContent // Or a dedicated "Cancelled" content component
          clientName={client.name}
          message="The enrichment process was cancelled."
          rawResponse={rawDebugResponse}
        />
      );
    default: // 'fallback_unexpected' or any other state
      return (
        <EnrichmentFallbackDialogContent
          clientName={client.name}
          message={derivedMessage || "Enrichment status is currently undetermined."}
          rawResponse={rawDebugResponse || initialLutraResponse}
        />
      );
  }
};
